import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import LiFiClient from '../../clients/LiFiClient'
import { PlugToken } from '../../interfaces/common'
import { EarnVault } from '../../interfaces/earn'
import { logger } from '../../logger'
import { maxBigInt } from '../../utils/bigint'
import useBalances from '../useBalances'
import useConfig from '../useConfig'
import useDebouncedState from '../useDebouncedState'
import useToken from '../useToken'
import useWallet from '../useWallet'

export type EarnDepositQuote = Awaited<ReturnType<LiFiClient['getDepositQuote']>>
export type DepositState = 'idle' | 'loading' | 'success' | 'error'

export const useEarnDeposit = (vault: EarnVault) => {
  const lifi = useMemo(() => new LiFiClient(), [])
  const { tokenPricesWithChange, evmChains } = useConfig()
  const { findBalance, refreshBalance } = useBalances()
  const { wallet } = useWallet()
  const { t } = useTranslation()

  const [debouncedAmount, setAmount, amount] = useDebouncedState('', 500)
  const [quote, setQuote] = useState<EarnDepositQuote | null>(null)
  const [quoteLoading, setQuoteLoading] = useState(false)
  const [depositState, setDepositState] = useState<DepositState>('idle')

  const primaryToken = vault.underlyingTokens[0]
  const lifiFeeToken = quote?.estimate.gasCosts?.[0]?.token
  const feeToken = useToken({ address: lifiFeeToken?.address, chain: lifiFeeToken?.chainId })
  const token = useToken({ address: primaryToken?.address, chain: vault.chainId })

  const numericAmount = Number(debouncedAmount) || 0
  const gasCost = quote?.estimate?.gasCosts?.[0]

  const tokenBalance = findBalance(token)?.balance || 0n
  const feeTokenBalance = findBalance(feeToken)?.balance || 0n

  const tokenPrice = token.id ? (tokenPricesWithChange[token.id]?.price ?? 0) : 0
  const amountUsd = numericAmount * tokenPrice
  const yearlyEarnings = amountUsd * (vault.analytics.apy.total / 100)
  const monthlyEarnings = yearlyEarnings / 12

  const maxAmount = useMemo(() => {
    if (gasCost && token.id === feeToken.id) {
      return maxBigInt(tokenBalance - BigInt(gasCost.amount), 0n)
    }

    return tokenBalance
  }, [tokenBalance, gasCost, token.id, feeToken.id])

  const error = useMemo(() => {
    if (!token.id) {
      return t('earn.deposit.unsupportedToken')
    }

    if (!numericAmount) {
      return t('swap.errors.no_amount')
    }

    const amountRaw = BigInt(Math.round(numericAmount * 10 ** token.decimals!))

    if (amountRaw > tokenBalance) {
      return t('earn.deposit.notEnoughBalance', { token: token.symbol })
    }

    if (!quote && !quoteLoading) {
      return t('earn.deposit.quoteFailed')
    }

    if (gasCost) {
      const gasAmount = BigInt(gasCost.amount)

      if (token.id === feeToken.id && amountRaw + gasAmount > tokenBalance) {
        return t('earn.deposit.notEnoughForGas', { token: token.symbol })
      } else if (gasAmount > feeTokenBalance) {
        return t('earn.deposit.notEnoughForGas', { token: feeToken.symbol })
      }
    }
  }, [token.id, debouncedAmount, tokenBalance, feeTokenBalance, feeToken, quote, quoteLoading, t])

  useEffect(() => {
    setQuote(null)

    if (numericAmount && token) {
      setQuoteLoading(true)

      const amountRaw = BigInt(Math.round(numericAmount * 10 ** token.decimals!))

      lifi
        .getDepositQuote(vault, amountRaw, wallet)
        .then(setQuote)
        .catch((e) => {
          logger.error('Failed to fetch deposit quote', { originalError: e })
          setQuote(null)
        })
        .finally(() => setQuoteLoading(false))
    }
  }, [debouncedAmount, token.id, vault, wallet])

  const executeDeposit = useCallback(async () => {
    if (!quote) return

    setDepositState('loading')

    try {
      await lifi.executeDeposit(quote, wallet, evmChains)

      setDepositState('success')
      refreshBalance(token as PlugToken)
    } catch (e) {
      logger.error('Failed to execute deposit', { originalError: e })
      setDepositState('error')
    }
  }, [quote, wallet, evmChains, lifi, token, refreshBalance])

  return {
    amount,
    setAmount,
    token: token as PlugToken,
    yearlyEarnings,
    monthlyEarnings,
    quote,
    quoteLoading,
    error,
    maxAmount,
    depositState,
    executeDeposit,
  }
}
